import {
  ArrowLeft,
  CalendarDays,
  Clock,
  Compass,
  MapPin,
  SlidersHorizontal,
  Swords,
  Users,
} from "lucide-react";
import { PhoneMock } from "../PhoneMock";

/**
 * App preview: the real Whistll "Open Runs" (Find a Run) screen.
 *
 * Mirrors apps/mobile/src/pages/Matches.jsx: "Open Runs" H1 with volt-
 * coloured second word, volt pill filter row, MatchCard rows with
 * status badge, time/format/distance chips, player fill bar, and
 * price/CTA row.
 */
export function FindARunMock({ className }: { className?: string }) {
  return (
    <PhoneMock className={className}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-1 text-[10px] font-semibold">
        <span>9:41</span>
        <span className="flex items-center gap-1 text-[9px]">● ● ●</span>
      </div>

      {/* Back link */}
      <div className="px-4 pt-3 pb-1">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#8899A6]">
          <ArrowLeft className="h-3 w-3" />
          Home
        </div>
      </div>

      {/* Title */}
      <div className="px-4 pb-2">
        <h1 className="text-[20px] font-black leading-tight tracking-tight text-[#F5F5DC]">
          Open <span className="text-[#FF6200]">Runs</span>
        </h1>
        <p className="mt-0.5 text-[10px] text-[#8899A6]">
          Find a pickup game near you
        </p>
      </div>

      {/* Pill filter row */}
      <div className="flex items-center gap-1.5 overflow-hidden px-4 pb-3">
        <SlidersHorizontal className="h-3 w-3 shrink-0 text-[#FF6200]" />
        <FilterPill icon={<MapPin className="h-2.5 w-2.5" />} label="London" />
        <FilterPill
          icon={<Compass className="h-2.5 w-2.5" />}
          label="10mi"
        />
        <FilterPill
          icon={<CalendarDays className="h-2.5 w-2.5" />}
          label="Today"
        />
        <FilterPill icon={<Clock className="h-2.5 w-2.5" />} label="Evening" />
      </div>

      {/* Match cards */}
      <div className="flex flex-col gap-2 px-4">
        <MatchCardMock
          title="5v5 Run at Main Hall"
          venue="Brixton Rec"
          status="Open"
          statusBg="rgba(255,98,0,0.1)"
          statusColor="#FF6200"
          time="Tonight · 19:00"
          format="5v5"
          distance="1.2 mi"
          joined={7}
          total={10}
          price="£8.50"
        />
        <MatchCardMock
          title="4v4 Quick Game"
          venue="Westway Sports"
          status="Forming"
          statusBg="rgba(251,191,36,0.12)"
          statusColor="#fbbf24"
          time="Tonight · 20:30"
          format="4v4"
          distance="2.4 mi"
          joined={5}
          total={8}
          price="£6.00"
        />
        <MatchCardMock
          title="3v3 Half Court"
          venue="Copper Box"
          status="Confirmed"
          statusBg="rgba(34,197,94,0.12)"
          statusColor="#4ade80"
          time="Tomorrow · 18:00"
          format="3v3"
          distance="3.8 mi"
          joined={6}
          total={6}
          price="£5.00"
        />
      </div>
    </PhoneMock>
  );
}

function FilterPill({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 rounded-full border border-[#FF6200] bg-[#FF6200] px-2 py-0.5 text-[9px] font-bold text-[#F5F5DC]">
      {icon}
      <span>{label}</span>
    </div>
  );
}

function MatchCardMock({
  title,
  venue,
  status,
  statusBg,
  statusColor,
  time,
  format,
  distance,
  joined,
  total,
  price,
}: {
  title: string;
  venue: string;
  status: string;
  statusBg: string;
  statusColor: string;
  time: string;
  format: string;
  distance: string;
  joined: number;
  total: number;
  price: string;
}) {
  const pct = Math.min(100, Math.round((joined / total) * 100));
  const isFull = joined >= total;
  return (
    <div className="rounded-2xl border border-[#1e2832] bg-[#131a20] p-3">
      {/* Top row: title + status badge */}
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[11px] font-bold text-[#F5F5DC]">
            {title}
          </p>
          <div className="mt-0.5 flex items-center gap-1">
            <MapPin className="h-2.5 w-2.5 shrink-0 text-[#4a5568]" />
            <span className="truncate text-[9px] text-[#8899A6]">{venue}</span>
          </div>
        </div>
        <span
          className="shrink-0 rounded-full px-2 py-0.5 text-[8px] font-bold"
          style={{ background: statusBg, color: statusColor }}
        >
          {status}
        </span>
      </div>

      {/* Chips row */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span className="flex items-center gap-0.5 text-[9px] text-[#8899A6]">
          <Clock className="h-2.5 w-2.5" /> {time}
        </span>
        <span className="rounded-full bg-[#FF6200]/10 px-1.5 py-0.5 text-[8px] font-semibold text-[#FF6200]">
          {format}
        </span>
        <span className="flex items-center gap-0.5 rounded-full bg-[#6496ff]/10 px-1.5 py-0.5 text-[8px] font-semibold text-[#6496ff]">
          <Compass className="h-2 w-2" /> {distance}
        </span>
      </div>

      {/* Player count + fill bar */}
      <div className="mb-2">
        <div className="mb-1 flex items-center gap-1">
          <Users className="h-2.5 w-2.5 text-[#4a5568]" />
          <span className="text-[9px] font-bold text-[#F5F5DC]">{joined}</span>
          <span className="text-[9px] text-[#4a5568]">/ {total} players</span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-[#1e2832]">
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: isFull
                ? "#4a5568"
                : "linear-gradient(90deg, #FF6200, #FF8C42)",
            }}
          />
        </div>
      </div>

      {/* Price + CTA */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-black text-[#FF6200]">
          {price}
          <span className="text-[9px] font-medium text-[#4a5568]">/player</span>
        </span>
        <span className="text-[9px] font-semibold text-[#4a5568]">
          {isFull ? "Full" : "Tap to join →"}
        </span>
      </div>
    </div>
  );
}
